import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@fantaz/shared/app-config';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { AuthServiceInjectionToken } from './auth';

@Injectable({
  providedIn: 'root',
})
export class SecurityValidationService {
  authService = inject(AuthServiceInjectionToken);

  constructor(private http: HttpClient) {}

  private handleError(error: HttpErrorResponse) {
    if (error.status === 511) {
      // TODO: this is the code we will use to trigger an action, e.g. ask the user to solve a captcha.
      //       For now silence the error until we collect enough information and decide how to handle it.
      console.warn('The user did not pass the security validation.');
    } else {
      // Something else happened but we won't bother the user with this.
      console.error(
        `Backend returned code ${error.status}, body was: `,
        error.error,
      );
    }

    return of(undefined); // Silence the error.
  }

  /**
   * Sends a v3 reCaptcha token to be validated.
   * We just needs to validate the response code:
   * - 200 OK   : validation passed.
   * - 511 ERROR: validation failed.
   *
   * @param {string} token See https://developers.google.com/recaptcha/docs/v3#programmatically_invoke_the_challenge
   *
   * @returns {Observable<void>}
   */
  reCaptchaValidationV3(token: string): Observable<void> {
    return this.authService.isLoggedIn().pipe(
      map((isLoggedIn) => {
        if (!isLoggedIn) {
          return `${environment.baseURL}/api/client/v3/security-validation`;
        }

        return `${environment.baseURL}/api/client/v3/auth-security-validation`;
      }),
      switchMap((url) =>
        this.http
          .post<void>(url, {
            token,
          })
          .pipe(catchError(this.handleError)),
      ),
    );
  }

  /**
   * Sends a v2 reCaptcha token to be validated.
   * We just needs to validate the response code:
   * - 200 OK   : validation passed.
   * - 511 ERROR: validation failed.
   *
   * @param {string} token
   *
   * @returns {Observable<void>}
   */
  reCaptchaValidationV2(token: string): Observable<void> {
    return this.authService.isLoggedIn().pipe(
      map((isLoggedIn) => {
        if (!isLoggedIn) {
          return `${environment.baseURL}/api/client/v2/security-validation`;
        }

        return `${environment.baseURL}/api/client/v2/auth-security-validation`;
      }),
      switchMap((url) =>
        this.http
          .post<void>(url, {
            token,
          })
          .pipe(catchError(this.handleError)),
      ),
    );
  }
}
